import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, Input, Label } from '@utima/ui';
import { memo, useEffect, useState } from 'react';

import { useFeedAttributeMutation } from './useFeedAttributeMutation';
import type { FeedAttributeRow } from './useFeedAttributesCols';

export type RenameAttributeDialogProps = {
  attribute: FeedAttributeRow | null;
  onClose: () => void;
};

/**
 * Renaming is a vocabulary edit, not a re-typing: the canonical name is the
 * uniqueness key the enrichment prompt reuses, so the old one is absorbed into
 * the aliases instead of being dropped. `dataType`, `unit` and `baseUnit` are
 * absent on purpose — changing a base unit would leave every already-stored
 * number measured in the old one.
 */
export const RenameAttributeDialog = memo(function RenameAttributeDialog({
  attribute,
  onClose,
}: RenameAttributeDialogProps) {
  const { t } = useLingui();
  const { rename, update } = useFeedAttributeMutation();

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    setName(attribute?.name ?? '');
    setDisplayName(attribute?.displayName ?? '');
  }, [attribute]);

  const handleSubmit = async () => {
    if (!attribute || !name.trim()) {
      return;
    }

    await rename({
      id: attribute.id,
      name: name.trim(),
      displayName: displayName.trim() || null,
    });

    onClose();
  };

  return (
    <Dialog.Root open={!!attribute} onOpenChange={onClose}>
      <Dialog.Content className='w-full max-w-[520px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Rename attribute</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              The previous canonical name is kept as an alias, so the enrichment
              model still maps its own wording onto this attribute instead of
              creating a second one.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='flex flex-col gap-4 py-2'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='attribute-display-name'>
              <Trans>Display name</Trans>
            </Label>
            <Input
              id='attribute-display-name'
              value={displayName}
              placeholder={t`Shown to people reviewing the catalog`}
              disabled={update.isPending}
              onChange={event => setDisplayName(event.target.value)}
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='attribute-name'>
              <Trans>Canonical name</Trans>
            </Label>
            <Input
              id='attribute-name'
              value={name}
              disabled={update.isPending}
              onChange={event => setName(event.target.value)}
            />
            <span className='text-xs text-muted-fg'>
              <Trans>
                Saved as a slug in the language of the feed — lowercase, no
                accents, underscores instead of spaces.
              </Trans>
            </span>
          </div>
        </div>

        <Dialog.Footer>
          <Button variant='secondary' onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-cta='admin-feed-attribute-rename-submit-button'
            variant='primary'
            loading={update.isPending}
            disabled={!name.trim()}
            onClick={handleSubmit}
          >
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
